// src/pages/debitNote/CreateDebitNotePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, PlusCircle, AlertCircle } from 'lucide-react';
import { issueDebitNote } from '../../services/api/procurement/debitNote';
import { getAllSuppliers } from '../../services/api/supplier';
import { getAllPurchaseOrders, getPurchaseOrderById } from '../../services/api/purchaseOrder';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import styles from './CreateDebitNotePage.module.css';

const CreateDebitNotePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [suppliers, setSuppliers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState('');
  const [sourceType, setSourceType] = useState('price_adjustment');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPurchaseOrders, setIsLoadingPurchaseOrders] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const purchaseOrderIdParam = searchParams.get('purchaseOrderId');

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const suppliersResult = await getAllSuppliers({ limit: 200 });
        setSuppliers(suppliersResult.suppliers || []);
        if (purchaseOrderIdParam) {
          const { purchaseOrder } = await getPurchaseOrderById(purchaseOrderIdParam);
          if (purchaseOrder) {
            setSupplierId(String(purchaseOrder.supplierId));
            setPurchaseOrders([purchaseOrder]);
            setPurchaseOrderId(String(purchaseOrder.id));
          }
        }
      } catch (err) {
        console.error('Failed to load debit note form data:', err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [purchaseOrderIdParam]);

  // Purchase Order is scoped to the selected supplier only — a debit note must belong to
  // that supplier's own order, not any order in the business.
  useEffect(() => {
    if (!supplierId) {
      setPurchaseOrders([]);
      setPurchaseOrderId('');
      return;
    }
    (async () => {
      try {
        setIsLoadingPurchaseOrders(true);
        const result = await getAllPurchaseOrders({ supplierId, limit: 200 });
        setPurchaseOrders(result.purchaseOrders || []);
        if (purchaseOrderIdParam && (result.purchaseOrders || []).some(po => String(po.id) === String(purchaseOrderIdParam))) {
          setPurchaseOrderId(String(purchaseOrderIdParam));
        }
      } catch (err) {
        console.error('Failed to load supplier purchase orders:', err);
      } finally {
        setIsLoadingPurchaseOrders(false);
      }
    })();
    if (!purchaseOrderIdParam) setPurchaseOrderId('');
  }, [supplierId, purchaseOrderIdParam]);

  const validateForm = () => {
    const nextErrors = {};

    if (!supplierId) nextErrors.supplierId = t('debitNote.supplierRequired');
    if (!purchaseOrderId) nextErrors.purchaseOrderId = t('debitNote.purchaseOrderRequired');
    if (!amount || Number(amount) <= 0) nextErrors.amount = t('debitNote.amountMustBePositive');
    if (!reason.trim()) nextErrors.reason = t('debitNote.reasonRequired');

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('debitNote.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        supplierId: Number(supplierId),
        purchaseOrderId: Number(purchaseOrderId),
        sourceType,
        amount: Number(amount),
        reason: reason.trim(),
      };

      const response = await issueDebitNote(payload);
      success(t('debitNote.debitNoteCreatedSuccessfully', { number: response.debitNote.debitNoteNumber }), {
        title: t('common.success'),
      });
      navigate(`/procurement/debit-notes/${response.debitNote.id}`);
    } catch (err) {
      console.error('Debit note creation error:', err);
      error(err.response?.data?.message || t('debitNote.failedToCreateDebitNote'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createDebitNotePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <PlusCircle className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('debitNote.createDebitNote')}</h1>
              <p>{t('debitNote.createDebitNoteDescription')}</p>
            </div>
          </div>
          <button type="button" onClick={() => navigate('/procurement/debit-notes')} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('debitNote.backToDebitNotes')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.debitNoteForm}>
        <div className={styles.formSection}>
          <div className={styles.sectionHeader}>
            <PlusCircle className={styles.sectionIcon} size={20} />
            <h2>{t('debitNote.debitNoteDetails')}</h2>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.fieldsGrid}>
              <div className={styles.fieldGroup}>
                <label>{t('debitNote.supplier')} *</label>
                <Dropdown
                  value={supplierId}
                  onChange={value => setSupplierId(value || '')}
                  options={suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name }))}
                  placeholder={t('debitNote.selectSupplier')}
                  searchable
                  disabled={isLoading}
                  className={errors.supplierId ? styles.error : ''}
                />
                {errors.supplierId && <div className={styles.errorText}><AlertCircle size={14} /> {errors.supplierId}</div>}
              </div>

              <div className={styles.fieldGroup}>
                <label>{t('debitNote.purchaseOrder')} *</label>
                <Dropdown
                  value={purchaseOrderId}
                  onChange={value => setPurchaseOrderId(value || '')}
                  options={purchaseOrders.map((po) => ({ value: po.id, label: `${po.purchaseOrderNumber} (${formatCurrency(po.grandTotal)})` }))}
                  placeholder={t('debitNote.selectPurchaseOrder')}
                  searchable
                  disabled={isLoading || isLoadingPurchaseOrders || !supplierId}
                  className={errors.purchaseOrderId ? styles.error : ''}
                />
                {errors.purchaseOrderId && <div className={styles.errorText}><AlertCircle size={14} /> {errors.purchaseOrderId}</div>}
              </div>
            </div>

            <div className={styles.fieldsGrid}>
              <div className={styles.fieldGroup}>
                <label>{t('debitNote.sourceType')}</label>
                <Dropdown
                  value={sourceType}
                  onChange={value => setSourceType(value || 'price_adjustment')}
                  options={[
                    { value: 'price_adjustment', label: t('debitNote.sourceType_price_adjustment') },
                    { value: 'correction', label: t('debitNote.sourceType_correction') },
                  ]}
                  allowClear={false}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>{t('debitNote.amount')} *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={`${styles.input} ${errors.amount ? styles.error : ''}`}
                  placeholder={t('debitNote.amountPlaceholder')}
                />
                {errors.amount && <div className={styles.errorText}><AlertCircle size={14} /> {errors.amount}</div>}
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <label>{t('debitNote.reason')} *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className={`${styles.textarea} ${errors.reason ? styles.error : ''}`}
                placeholder={t('debitNote.reasonPlaceholder')}
              />
              {errors.reason && <div className={styles.errorText}><AlertCircle size={14} /> {errors.reason}</div>}
            </div>
          </div>
        </div>

        <div className={styles.formActions}>
          <button type="button" onClick={() => navigate('/procurement/debit-notes')} className={styles.secondaryButton}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={isSubmitting} className={styles.primaryButton}>
            {isSubmitting ? t('common.loading') : t('debitNote.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateDebitNotePage;
